import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import ZazuLogo from '@/components/ZazuLogo';
import OnboardingTour from '@/components/OnboardingTour';
import DailyTasksWidget from '@/components/DailyTasksWidget';
import StreakWidget from '@/components/StreakWidget';
import {
  Play, Star, Trophy, LogOut, BookOpen, Music, Palette, Calculator, 
  Volume2, Sparkles, Heart, Flame, ShieldCheck, Brain
} from 'lucide-react';

const StudentDashboard = () => {
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [parentRoleHint, setParentRoleHint] = useState(null);

  const childId = user?.studentChildId || localStorage.getItem('student-child-id');

  useEffect(() => {
    if (childId) {
      fetchChildData();
      fetchActivities();
    }
    // Best-effort role probe — tells us if the parent behind this student session
    // can access admin/employee. Only then do we show "Switch to admin".
    (async () => {
      try {
        const r = await api.get('/auth/parent-role-hint');
        setParentRoleHint(r.data);
      } catch (err) { void err; }
    })();
  }, [childId]);

  const fetchChildData = async () => {
    try {
      const response = await api.get(`/children/${childId}`);
      setChild(response.data);
    } catch (error) {
      console.error('Failed to fetch child:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const childData = await api.get(`/children/${childId}`);
      const ageGroup = childData.data.age_group;
      
      // Get age-appropriate activities
      const response = await api.get(`/curriculum/infant-toddler?age_group=${ageGroup}`);
      setActivities([...response.data.videos, ...response.data.games].slice(0, 8));
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('See you next time! 👋');
  };

  const handleSwitchToAdmin = () => {
    // Ends the child session and drops the parent at the login screen with a
    // clear reason banner (LoginPage listens for ?reauth=1).
    logout();
    navigate('/login?reauth=1');
  };

  const getActivityIcon = (activity) => {
    const type = activity.activity_type;
    const subject = activity.subject;
    
    if (type === 'video') return <Play className="w-8 h-8" />;
    if (subject === 'music') return <Music className="w-8 h-8" />;
    if (subject === 'alphabets') return <BookOpen className="w-8 h-8" />;
    if (subject === 'numbers' || subject === 'math') return <Calculator className="w-8 h-8" />;
    if (subject === 'shapes') return <Palette className="w-8 h-8" />;
    return <Sparkles className="w-8 h-8" />;
  };

  const getActivityColor = (index) => {
    const colors = [
      'from-pink-400 to-rose-500',
      'from-purple-400 to-indigo-500',
      'from-blue-400 to-cyan-500',
      'from-emerald-400 to-teal-500',
      'from-amber-400 to-orange-500',
      'from-red-400 to-pink-500',
      'from-violet-400 to-purple-500',
      'from-cyan-400 to-blue-500',
    ];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-100 via-pink-50 to-amber-50 flex items-center justify-center">
        <motion.div
          animate={{ scale: [1, 1.2, 1], rotate: [0, 360] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Star className="w-16 h-16 text-amber-400" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-pink-50 to-amber-50">
      {/* Onboarding Tour for student */}
      <OnboardingTour userType="student" />
      
      {/* Fun Header */}
      <header className="p-4 flex items-center justify-between">
        <ZazuLogo size="lg" />
        <div className="flex items-center gap-2" data-tour="achievements">
          {parentRoleHint?.parent_can_admin && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleSwitchToAdmin}
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-white/70 hover:bg-white border-emerald-200 text-emerald-700 font-semibold"
              data-testid="switch-to-admin-btn"
              title="Sign in to your admin/employee account"
            >
              <ShieldCheck className="w-4 h-4" />
              Switch to admin
            </Button>
          )}
          {parentRoleHint?.parent_can_admin && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSwitchToAdmin}
              className="sm:hidden rounded-full bg-white/50 hover:bg-white"
              data-testid="switch-to-admin-btn-mobile"
              aria-label="Switch to admin"
              title="Switch to admin account"
            >
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="icon"
            className="rounded-full bg-white/50 hover:bg-white"
            onClick={handleLogout}
          >
            <LogOut className="w-5 h-5 text-slate-600" />
          </Button>
        </div>
      </header>

      {/* Welcome Section */}
      <main className="max-w-4xl mx-auto px-4 pb-8">
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="relative inline-block mb-4">
            <motion.div 
              className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white text-4xl font-bold shadow-lg"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              {child?.name?.charAt(0).toUpperCase() || '🌟'}
            </motion.div>
            <motion.div
              className="absolute -top-2 -right-2"
              animate={{ rotate: [0, 20, -20, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Star className="w-8 h-8 text-amber-400 fill-amber-400" />
            </motion.div>
          </div>
          <h1 className="font-fredoka text-4xl text-slate-800 mb-2">
            Hi, {child?.name}! 🎉
          </h1>
          <p className="text-xl text-slate-600">What do you want to learn today?</p>
        </motion.div>

        {/* Stats Bar */}
        <div className="flex justify-center gap-6 mb-8">
          <motion.div 
            className="flex items-center gap-2 bg-white/80 backdrop-blur px-4 py-2 rounded-full shadow"
            whileHover={{ scale: 1.05 }}
          >
            <Star className="w-6 h-6 text-amber-500 fill-amber-500" />
            <span className="font-bold text-lg">{child?.total_xp || 0} XP</span>
          </motion.div>
          <motion.button
            type="button"
            onClick={() => childId && navigate(`/trophy/${childId}`)}
            className="flex items-center gap-2 bg-white/80 backdrop-blur px-4 py-2 rounded-full shadow hover:bg-white transition-colors"
            whileHover={{ scale: 1.05 }}
            data-testid="dashboard-open-trophy-room"
            title="Open Trophy Room"
          >
            <Trophy className="w-6 h-6 text-amber-500" />
            <span className="font-bold text-lg">{child?.badges?.length || 0} Badges</span>
          </motion.button>
          <motion.div 
            className="flex items-center gap-2 bg-white/80 backdrop-blur px-4 py-2 rounded-full shadow"
            whileHover={{ scale: 1.05 }}
          >
            <Heart className="w-6 h-6 text-rose-500 fill-rose-500" />
            <span className="font-bold text-lg">Level {child?.current_level || 1}</span>
          </motion.div>
        </div>

        {/* Structured Learning - Skill Lessons (Primary CTA) */}
        <div className="mb-8" data-testid="skill-lessons-section">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <motion.div
              initial={{ x: -30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <Link to={`/skill-lessons/${childId}`}>
                <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-400 to-teal-500 text-white hover:shadow-xl transition-shadow cursor-pointer overflow-hidden">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-3xl shrink-0">
                      📖
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Start Learning</h3>
                      <p className="text-sm opacity-90">Letters, Sounds & Reading</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
            <motion.div
              initial={{ x: 30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <Link to={`/skill-lessons/${childId}`}>
                <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-400 to-indigo-500 text-white hover:shadow-xl transition-shadow cursor-pointer overflow-hidden">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-3xl shrink-0">
                      🔢
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Start Learning</h3>
                      <p className="text-sm opacity-90">Counting, Numbers & Math</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Daily Tasks Section */}
        {childId && (
          <div className="mb-8">
            <DailyTasksWidget 
              childId={childId} 
              onTaskComplete={() => fetchChildData()}
            />
          </div>
        )}

        {/* Streak Widget */}
        {childId && (
          <div className="mb-8">
            <StreakWidget childId={childId} />
          </div>
        )}

        {/* Wonder Room shortcut — always visible, kids love the surprise questions */}
        <motion.div
          className="mb-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Link to={`/critical-thinking/${childId}`} data-testid="dashboard-open-wonder-room">
            <Card className="overflow-hidden cursor-pointer border-0 shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-r from-fuchsia-500 via-violet-500 to-indigo-500 text-white">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <Brain className="w-8 h-8" />
                </div>
                <div className="flex-1">
                  <h3 className="font-fredoka text-lg font-bold">Wonder Room</h3>
                  <p className="text-sm text-white/90">Try a &ldquo;why is the sky blue?&rdquo; question — think, then chat with Zazu about it.</p>
                </div>
                <Sparkles className="w-6 h-6 shrink-0" />
              </CardContent>
            </Card>
          </Link>
        </motion.div>

        {/* Activity Cards Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {activities.map((activity, index) => (
            <motion.div
              key={activity.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.05, rotate: [-1, 1, -1, 0] }}
              whileTap={{ scale: 0.95 }}
            >
              <Link to={`/activity/${activity.id}?child=${childId}`}>
                <Card className="overflow-hidden cursor-pointer h-full border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <div className={`h-28 bg-gradient-to-br ${getActivityColor(index)} flex items-center justify-center text-white`}>
                    {getActivityIcon(activity)}
                  </div>
                  <CardContent className="p-3 text-center">
                    <h3 className="font-bold text-sm text-slate-800 line-clamp-2">{activity.title}</h3>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                      <span className="text-xs text-slate-500">+{activity.xp_reward} XP</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Big Play Button */}
        <motion.div 
          className="mt-8 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Link to={`/learn/${childId}`}>
            <Button 
              size="lg" 
              className="h-16 px-12 text-xl rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-lg"
            >
              <Play className="w-6 h-6 mr-2" />
              Start Learning!
            </Button>
          </Link>
        </motion.div>
      </main>

      {/* Fun floating elements */}
      <div className="fixed bottom-4 left-4 pointer-events-none">
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          <span className="text-4xl">🦜</span>
        </motion.div>
      </div>
    </div>
  );
};

export default StudentDashboard;
